import { ICustomMedication } from "../../../@types/admin/AdminMedication";
import { useDeactivateCustomMedication } from "../../../hooks/mutations/admin/customMedications";
import { useToast } from "../../../hooks/useToast";
import { errorToastMessage } from "../../../utils/HttpError";
import {
  FilterButton,
  ModalBodyText,
  ModalContainer,
  ModalForm,
  ModalFormActions,
  ModalTitle,
  WarningNotice,
} from "../shared";

interface IDeactivateCustomMedicationModalProps {
  medication: ICustomMedication;
  hospitalGroupId: number;
  onDone: () => void;
}

/**
 * Deactivates a custom medication (EN-5240). Not a delete: patients who
 * already added it keep it on their medication list and schedule, and it can
 * be reactivated from the list.
 */
export function DeactivateCustomMedicationModal({
  medication,
  hospitalGroupId,
  onDone,
}: IDeactivateCustomMedicationModalProps) {
  const { showToast } = useToast();
  const { deactivateCustomMedication, isDeactivatingCustomMedication } =
    useDeactivateCustomMedication();

  const handleDeactivate = async () => {
    try {
      await deactivateCustomMedication({
        hospitalGroupId,
        id: medication.id,
      });
      showToast(`${medication.name} was deactivated.`, "success");
      onDone();
    } catch (error) {
      showToast(
        errorToastMessage(
          error,
          "There was an error deactivating this custom medication."
        ),
        "warning"
      );
    }
  };

  return (
    <ModalContainer>
      <ModalTitle>Deactivate custom medication</ModalTitle>
      <ModalForm>
        <ModalBodyText>
          Deactivate <strong>{medication.name}</strong>?
        </ModalBodyText>
        <WarningNotice>
          Patients will no longer find it in the medication search. Patients who
          already added it keep it on their list and schedule, and it can be
          reactivated from this list — nothing is deleted.
        </WarningNotice>
        <ModalFormActions>
          <FilterButton
            variant="secondary"
            disabled={isDeactivatingCustomMedication}
            onClick={onDone}
          >
            Cancel
          </FilterButton>
          <FilterButton
            variant="primary"
            disabled={isDeactivatingCustomMedication}
            onClick={handleDeactivate}
          >
            {isDeactivatingCustomMedication ? "Deactivating..." : "Deactivate"}
          </FilterButton>
        </ModalFormActions>
      </ModalForm>
    </ModalContainer>
  );
}
